import {
  Children,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import { TabsProvider } from './Tabs.provider'
import styled from 'styled-components'
import { TabItem } from './TabItem'
import { HStack } from '@musinsa/mwms-ds'
import { colorSystem } from '@/styles/theme'

interface TabsProps<T extends string> extends PropsWithChildren, HTMLAttributes<HTMLDivElement> {
  value: T
  onTabChange: (value: T) => void
}

function _Tabs<T extends string>({ children, value, onTabChange, ...htmlDivProps }: TabsProps<T>, ref: ForwardedRef<HTMLDivElement>) {
  const childrenWrapRef = useRef<HTMLDivElement>(null)

  const [underlineCSS, setUnderlineCSS] = useState<CSSProperties>({})

  useEffect(() => {
    const childrenWrap = childrenWrapRef.current
    if (!childrenWrap) return

    const selectedIdx = Children.toArray(children).findIndex((child) => {
      return (child as ReactElement).props.value === value
    })

    const selectedTab = childrenWrap.children[selectedIdx] as HTMLElement

    if (selectedTab) {
      setUnderlineCSS({
        width: selectedTab.offsetWidth,
        left: selectedTab.offsetLeft,
      })
    }
  }, [value, children])

  return (
    <TabsProvider<T> value={{ selectValue: value, onTabChange }}>
      <TabsWrap role="tablist" ref={ref} {...htmlDivProps}>
        <HStack gap={20} ref={childrenWrapRef}>
          {children}
        </HStack>
        <UnderLine style={underlineCSS} />
      </TabsWrap>
    </TabsProvider>
  )
}

const TabsWrap = styled.div`
  position: relative;

  display: flex;
  gap: 20px;
`

const UnderLine = styled.div`
  height: 2px;
  background-color: ${colorSystem.text.common.primary};
  transition: all 0.2s ease-in-out;

  position: absolute;
  bottom: 0;
`

const Tabs = forwardRef(_Tabs) as <T extends string>(props: TabsProps<T> & { ref?: ForwardedRef<HTMLDivElement> }) => ReturnType<typeof _Tabs>

const TabsCompound = Object.assign(Tabs, {
  Item: TabItem,
})

export { TabsCompound as Tabs }
